/**
 * Sound Alert Utility
 * Plays notification sounds for order status changes
 * Uses Web Audio API for lightweight, cross-platform audio
 */

let audioContext: AudioContext | null = null;

/**
 * Initialize audio context (requires user interaction)
 */
function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;

  if (!audioContext) {
    try {
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('Web Audio API not supported', error);
      return null;
    }
  }

  return audioContext;
}

/**
 * Play a simple notification beep
 * @param frequency - Hz (e.g., 800 for high beep, 400 for low)
 * @param duration - milliseconds
 */
function playBeep(frequency: number, duration: number): void {
  const ctx = getAudioContext();
  if (!ctx) return;

  try {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.1, ctx.currentTime); // Soft volume
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration / 1000);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + duration / 1000);
  } catch (error) {
    console.warn('Failed to play sound', error);
  }
}

/**
 * Sound patterns for different order events
 */
export const orderSounds = {
  /**
   * New order created - Single upward beep
   */
  orderPlaced: () => {
    playBeep(600, 150);
  },

  /**
   * Order accepted - Two quick beeps
   */
  orderAccepted: () => {
    playBeep(700, 100);
    setTimeout(() => playBeep(900, 100), 120);
  },

  /**
   * Order ready for pickup - Rising tone
   */
  orderReady: () => {
    playBeep(600, 80);
    setTimeout(() => playBeep(750, 80), 100);
    setTimeout(() => playBeep(900, 120), 200);
  },

  /**
   * Rider assigned - Short confirmation
   */
  riderAssigned: () => {
    playBeep(800, 150);
  },

  /**
   * Out for delivery - Cheerful double beep
   */
  outForDelivery: () => {
    playBeep(700, 100);
    setTimeout(() => playBeep(900, 150), 150);
  },

  /**
   * Delivered - Success chime
   */
  delivered: () => {
    playBeep(600, 100);
    setTimeout(() => playBeep(800, 100), 120);
    setTimeout(() => playBeep(1000, 200), 240);
  },

  /**
   * Cancelled - Low descending tone
   */
  cancelled: () => {
    playBeep(500, 150);
    setTimeout(() => playBeep(350, 200), 180);
  },
};

/**
 * Check if sound is enabled (can be controlled by user settings)
 */
export function isSoundEnabled(): boolean {
  if (typeof localStorage === 'undefined') return true;
  const setting = localStorage.getItem('order-sounds-enabled');
  return setting !== 'false'; // Default to enabled
}

/**
 * Toggle sound setting
 */
export function toggleSound(enabled: boolean): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem('order-sounds-enabled', String(enabled));
  }
}






